import { useAnalyticsExposure } from '../../hooks/useAnalyticsExposure';
import { notificationErrorCategory } from '../../utils/uiAnalytics';
import React from 'react';
import { Box, Typography, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationAlertProps {
  type: NotificationType;
  message: string;
  description?: string;
  onClose?: () => void;
  sx?: any;
  mode?: 'inline' | 'toast';
}
const NotificationAlert: React.FC<NotificationAlertProps> = ({
  type,
  message,
  description,
  onClose,
  sx,
  mode = 'inline'
}) => {
  useAnalyticsExposure('ui_error', `notification_${mode}`, type === 'error', `${message}\n${description || ''}`, notificationErrorCategory(message));
  const theme = useTheme();
  const isCompactNotification = useMediaQuery(theme.breakpoints.down('sm'));
  const notificationRowHeightPx = isCompactNotification ? 21 : 23;
  const circleSize = `${notificationRowHeightPx}px`;
  const borderWidth = isCompactNotification ? '1.5px' : '2px';
  const titleFontSize = isCompactNotification ? '13px' : "var(--type-small)";
  const descriptionFontSize = isCompactNotification ? '12px' : "var(--type-caption)";
  const contentGap = isCompactNotification ? '6px' : '8px';
  const closeIconSize = isCompactNotification ? '14px' : '16px';

  // Config for icons and colors based on type
  const getConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <Box sx={{
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            border: `${borderWidth} solid #4CAF50`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
              <Typography sx={{
              color: '#4CAF50',
              fontSize: isCompactNotification ? '14px' : "var(--type-body)",
              fontWeight: 'bold',
              lineHeight: 1,
              fontFamily: 'Arial, sans-serif'
            }}>
                ✓
              </Typography>
            </Box>
        };
      case 'error':
        return {
          icon: <Box sx={{
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            border: `${borderWidth} solid #E74C3C`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
              <Typography sx={{
              color: '#E74C3C',
              fontSize: isCompactNotification ? '13px' : "var(--type-small)",
              fontWeight: 'bold',
              lineHeight: 1,
              fontFamily: 'Arial, sans-serif'
            }}>
                !
              </Typography>
            </Box>
        };
      case 'warning':
        return {
          icon: <Box sx={{
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            border: `${borderWidth} solid #FF9800`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
              <WarningAmberIcon sx={{
              color: '#FF9800',
              width: isCompactNotification ? '14px' : '15px',
              height: isCompactNotification ? '14px' : '15px'
            }} />
            </Box>
        };
      case 'info':
      default:
        return {
          icon: <Box sx={{
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            border: `${borderWidth} solid #2196F3`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
              <Typography sx={{
              color: '#2196F3',
              fontSize: isCompactNotification ? '13px' : "var(--type-small)",
              fontWeight: 'bold',
              lineHeight: 1,
              fontFamily: 'Arial, sans-serif'
            }}>
                i
              </Typography>
            </Box>
        };
    }
  };
  const config = getConfig();

  // Common base styles
  const commonStyles = {
    display: 'flex',
    background: '#FFFFFF',
    borderRadius: '7px',
    boxSizing: 'border-box',
    maxWidth: '100%'
  };

  // Styles based on mode
  const modeStyles = mode === 'toast' ? {
    width: 'min(420px, calc(100vw - 32px))',
    padding: isCompactNotification ? '10px 14px' : '12px 21px',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '4px',
    boxShadow: '3px 4px 5.8px 0 rgba(0, 0, 0, 0.25)'
  } : {
    width: 'min(455px, calc(100vw - 32px))',
    minWidth: 0,
    maxWidth: 'calc(100vw - 32px)',
    padding: isCompactNotification ? '8px 12px' : '8px 16px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: isCompactNotification ? '12px' : '16px',
    boxShadow: '3px 4px 4px 0px rgba(0, 0, 0, 0.25)'
  };
  return <Box sx={{
    ...commonStyles,
    ...modeStyles,
    ...sx
  }}> 
     
      <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: contentGap,
      width: '100%'
    }}>
        {config.icon}
        
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0
      }}>
          {/* Title / Message */}
          <Typography sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 600,
          fontSize: titleFontSize,
          fontStyle: 'normal',
          color: 'var(--Font, #363434)',
          lineHeight: `${notificationRowHeightPx}px`,
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        }}>
            {message}
          </Typography>

          {/* Description / Detail */}
          {description && <Typography sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 400,
          fontSize: descriptionFontSize,
          fontStyle: 'normal',
          color: 'var(--Font, #363434)',
          lineHeight: 'normal',
          mt: 0.5,
          whiteSpace: 'pre-line',
          wordBreak: 'break-word'
        }}>
              {description}
            </Typography>}
        </Box>

        {onClose && <IconButton onClick={onClose} size="small" sx={{
        padding: 0,
        color: '#363434',
        alignSelf: 'flex-start',
        height: `${notificationRowHeightPx}px`,
        width: `${notificationRowHeightPx}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }}>
            <CloseIcon sx={{
          width: closeIconSize,
          height: closeIconSize
        }} />
          </IconButton>}
      </Box>
    </Box>;
};
export default NotificationAlert;
